import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, FormControlLabel, Paper, Radio, RadioGroup, TextField, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import api from '../../api/axios';
import { RootState } from '../../store';

const UnsubscribePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  // Get token and email from URL for guest users
  const token = searchParams.get('token');
  const email = searchParams.get('email');

  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);

  const [reasonType, setReasonType] = useState('NO_REASON');
  const [customText, setCustomText] = useState('');
  const [completed, setCompleted] = useState(false);
  const [loading, setLoading] = useState(false);

  const reasons = [
    { value: 'TOO_FREQUENT', label: 'Ich erhalte zu viele E-Mails' },
    { value: 'NO_LONGER_INTERESTED', label: 'Ich habe kein Interesse mehr' },
    { value: 'BORING_CONTENT', label: 'Der Inhalt ist nicht relevant für mich' },
    { value: 'CUSTOM_REASON', label: 'Sonstiges (bitte angeben)' },
    { value: 'NO_REASON', label: 'Keine Angabe' },
  ];

  const handleConfirm = async () => {
    if (!isAuthenticated && (!token || !email)) return;

    setLoading(true);
    try {
      const payload = {
        reasonType,
        customReason: reasonType === 'CUSTOM_REASON' ? customText : null,
        ...(isAuthenticated ? {} : { email, token })
      };

      const endpoint = isAuthenticated ? '/profile/newsletter/unsubscribe' : '/newsletter/unsubscribe';
      await api.post(endpoint, payload);

      setCompleted(true);
    } catch (error) {
      // Silent error handling as per security style
    } finally {
      setLoading(false);
    }
  };

  const isButtonDisabled = loading ||
      (!isAuthenticated && (!token || !email)) ||
      (reasonType === 'CUSTOM_REASON' && !customText.trim());

  if (completed) {
    return (
        <div className="min-h-[94vh] flex items-center justify-center bg-white dark:bg-neutral-950 px-4">
          <Paper className="p-10 max-w-md w-full text-center bg-white dark:bg-neutral-900 border border-gray-100 dark:border-neutral-800 shadow-2xl rounded-3xl animate-in fade-in zoom-in duration-500">
            <div className="mb-6 flex justify-center">
              <div className="h-16 w-16 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center text-emerald-600 dark:text-emerald-400">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Abgemeldet</h1>
            <Typography className="text-gray-500 dark:text-neutral-400">
              Du wurdest erfolgreich aus dem Newsletter entfernt. Du kannst dieses Fenster nun schließen.
            </Typography>
          </Paper>
        </div>
    );
  }

  return (
      <div className="min-h-[94vh] flex items-center justify-center bg-white dark:bg-neutral-950 px-4">
        <Paper className="p-8 max-w-md w-full flex flex-col space-y-8 bg-white dark:bg-neutral-900 border border-gray-100 dark:border-neutral-800 shadow-2xl rounded-2xl transition-all duration-300">
          <div className="space-y-2">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Newsletter abbestellen</h1>
            <Typography className="text-sm text-gray-500 dark:text-neutral-400">
              Möchtest du dich wirklich vom MoonSkins.GG Newsletter abmelden? Verrate uns optional den Grund:
            </Typography>
          </div>

          <div className="space-y-4">
            <RadioGroup
                value={reasonType}
                onChange={(e) => setReasonType(e.target.value)}
                className="space-y-1"
            >
              {reasons.map((r) => (
                  <div
                      key={r.value}
                      className={`flex items-center px-4 rounded-xl border transition-all ${
                          reasonType === r.value
                              ? 'border-blue-500 bg-blue-50/30 dark:bg-blue-900/10'
                              : 'border-gray-100 dark:border-neutral-800 bg-gray-50/50 dark:bg-neutral-800/40'
                      }`}
                  >
                    <FormControlLabel
                        value={r.value}
                        className="w-full py-1"
                        control={<Radio size="small" />}
                        label={<span className="text-sm font-medium text-gray-700 dark:text-neutral-200">{r.label}</span>}
                    />
                  </div>
              ))}
            </RadioGroup>

            <div className={`grid transition-all duration-300 ease-in-out ${
                reasonType === 'CUSTOM_REASON' ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
            }`}>
              <div className="overflow-hidden">
                <TextField
                    fullWidth
                    multiline
                    rows={3}
                    variant="outlined"
                    placeholder="Dein Grund..."
                    value={customText}
                    onChange={(e) => setCustomText(e.target.value)}
                    className="dark:bg-neutral-800/50"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                      }
                    }}
                />
              </div>
            </div>
          </div>

          <Button
              fullWidth
              variant="contained"
              onClick={handleConfirm}
              disabled={isButtonDisabled}
              disableElevation
              className="h-12 rounded-xl font-bold bg-dark-primary active:scale-[0.98] transition-all"
          >
            {loading ? 'Wird verarbeitet...' : 'Abmeldung bestätigen'}
          </Button>

        </Paper>
      </div>
  );
};

export default UnsubscribePage;